import * as React from "react";
import { motion } from "framer-motion";
import clsx from "clsx";
import { MarkdownRenderer } from "@/components/feature/markdown/markdownRenderer";
import { ChatMessageBubble } from "@/components/chat/ChatMessageBubble";
import { CHAT_ANIMATION, MESSAGE_STYLES } from "@/components/chat/constants";
import { useArtefactExtraction } from "@/hooks/useArtefactExtraction";
import { useStreamingText } from "@/hooks/useStreamingText";
import { isLetterArtefact } from "@/modules/chat/chat-ui.types";
import { OpenChatMessage } from "@/types/open-chat";
import { OpenChatMessageRowImprovementEditor } from "./message/messageRowImprovementEditor";
import { SearchResultsBox } from "./SearchResultsBox";

interface Props {
  message: OpenChatMessage;
  allowImprovement?: boolean;
  allowCopy?: boolean;
  isImprovement?: boolean;
  onImprovePrompt?: () => void;
  onWebCitationClick?: (sourceIds: number[], messageId?: string) => void;
  onSourcesButtonClick?: (messageId?: string) => void;
  hideIcons?: boolean;
}

export const ChatMessage = ({
  message,
  allowImprovement,
  allowCopy,
  isImprovement,
  onImprovePrompt,
  onWebCitationClick,
  onSourcesButtonClick,
  hideIcons,
}: Props) => {
  const [editedImprovedPrompt, setEditedImprovedPrompt] = React.useState<string>();
  const [isEdit, setIsEdit] = React.useState(false);

  const isStreamingMessage = message.type === "ai" && !message.completed;
  const rawContent = editedImprovedPrompt || message.content;
  const content = useStreamingText(rawContent, isStreamingMessage);
  const [processedContent, inlineArtefacts] = useArtefactExtraction(content);
  const processedMessage = React.useMemo(
    () => ({ ...message, content: processedContent }),
    [message, processedContent],
  );

  if (isImprovement) {
    return (
      <motion.div
        className="w-full"
        initial={CHAT_ANIMATION.fadeIn}
        animate={CHAT_ANIMATION.visible}
        exit={CHAT_ANIMATION.fadeOut}
        transition={CHAT_ANIMATION.spring}
      >
        <div
          className={clsx(MESSAGE_STYLES.ai, {
            "whitespace-pre-wrap border-2 border-gray-500": isEdit,
          })}
          contentEditable={isEdit}
          onBlur={t => setEditedImprovedPrompt(t.currentTarget.innerHTML)}
        >
          {isEdit ? (
            content
          ) : (
            <MarkdownRenderer
              content={processedContent}
              evidences={[]}
              processCitations={false}
              inlineArtefacts={inlineArtefacts}
            />
          )}
        </div>

        {message.completed && !hideIcons && (
          <OpenChatMessageRowImprovementEditor setIsEdit={setIsEdit} content={content} messageId={message.id} />
        )}
      </motion.div>
    );
  }

  const showSearchBox = message.isSearching || !!message.searchSources;
  const hasContent = !!message.content;
  const hasLetterArtefact = inlineArtefacts.some(isLetterArtefact);

  return (
    <>
      {showSearchBox && <SearchResultsBox searchSources={message.searchSources} isSearching={!!message.isSearching} />}
      {hasContent && (
        <ChatMessageBubble
          message={processedMessage}
          inlineArtefacts={inlineArtefacts}
          source="open_chat"
          webSearchSources={message.searchSources?.sources}
          onWebCitationClick={onWebCitationClick}
          onSourcesButtonClick={onSourcesButtonClick}
          onImprovePrompt={onImprovePrompt}
          hideActions={hideIcons}
          actions={{
            showImprove: allowImprovement && message.type === "human",
            showAddToLibrary: allowImprovement && message.type === "human",
            showCopy: allowCopy && message.type === "ai" && !hasLetterArtefact,
            showSources: allowCopy && message.type === "ai" && !hasLetterArtefact,
          }}
        />
      )}
    </>
  );
};
